import { round } from '@gozoki/tools'

export type DesiredTrend = 'up' | 'down'
export type ComparisonTone = 'favorable' | 'unfavorable' | 'neutral'
export type ComparisonDirection = 'up' | 'down' | 'neutral'

export type ComparisonUnit = string | ((displayedValue: number) => string)

export interface ComparisonPresentation {
  direction: ComparisonDirection
  tone: ComparisonTone
  label: string
}

export const NOT_COMPUTABLE_LABEL = 'NC'

export const POINTS_UNIT: ComparisonUnit = (displayedValue) =>
  Math.abs(displayedValue) <= 1 ? ' pt' : ' pts'

export const SAME_WEEKDAYS_MAX_WINDOW_DAYS = 6

/**
 * Décalage de la fenêtre comparée : sa propre durée, sauf ≤ 6 jours où l'on compare aux mêmes jours
 * de la semaine précédente. Un décalage de 7 signifie donc toujours « semaine précédente » (S-1).
 * Aligné sur le backend `computeDefaultComparisonPeriod`.
 */
export function comparisonShiftDays(windowDays: number): number {
  return windowDays <= SAME_WEEKDAYS_MAX_WINDOW_DAYS ? 7 : windowDays
}

export const COMPARISON_TEXT_CLASS_BY_TONE: Record<ComparisonTone, string> = {
  favorable: 'text-[#4F6A47]',
  unfavorable: 'text-[#EF8031]',
  neutral: 'text-[#6B6B6B]',
}

export function formatComparisonValue(value: number | null, unit: ComparisonUnit = '%'): string {
  if (value === null) {
    return NOT_COMPUTABLE_LABEL
  }

  const fractionDigits = Math.abs(value) < 1 ? 1 : 0
  const displayedValue = round(value, fractionDigits)
  const formattedValue = displayedValue.toLocaleString('fr-FR', {
    maximumFractionDigits: fractionDigits,
  })
  const resolvedUnit = typeof unit === 'function' ? unit(displayedValue) : unit

  return `${value >= 0 ? '+' : ''}${formattedValue}${resolvedUnit}`
}

export function getComparisonPresentation({
  value,
  desiredTrend,
  unit,
}: {
  value: number | null
  desiredTrend: DesiredTrend
  unit?: ComparisonUnit
}): ComparisonPresentation {
  if (value === null) {
    return {
      direction: 'neutral',
      tone: 'neutral',
      label: NOT_COMPUTABLE_LABEL,
    }
  }

  const direction: ComparisonDirection = value >= 0 ? 'up' : 'down'
  const isFavorable = value === 0 || direction === desiredTrend

  return {
    direction,
    tone: isFavorable ? 'favorable' : 'unfavorable',
    label: formatComparisonValue(value, unit),
  }
}
